import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { api } from "./api";

export interface CurrentUser {
  id: string;
  email: string;
  isSuperadmin?: boolean;
  createdAt?: string | null;
}

export interface SetupStatus {
  seeded: boolean;
}

export function useSetupStatus() {
  return useQuery({
    queryKey: ["setup-status"],
    queryFn: () => api.get<SetupStatus>("/setup/status"),
    staleTime: Infinity,
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const r = await api.get<{ user: CurrentUser }>("/auth/me");
        return r.user;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSettled: () => {
      // Seed null directly so RequireAuth bounces to /login immediately
      // (invalidate alone doesn't await the refetch).
      qc.setQueryData(["me"], null);
      qc.removeQueries({ queryKey: ["orgs"] });
    },
  });
}

/**
 * Nest inside RequireAuth (so `me` is already resolved). Bounces non-
 * superadmins back to the dashboard rather than rendering a 403 page —
 * they shouldn't be able to tell the route exists.
 */
export function RequireSuperadmin({ children }: { children: ReactNode }) {
  const me = useCurrentUser();
  if (me.isLoading) {
    return <div className="h-screen grid place-items-center text-subtle-foreground text-sm">Loading…</div>;
  }
  if (!me.data?.isSuperadmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const setup = useSetupStatus();
  const me = useCurrentUser();
  const loc = useLocation();

  if (setup.isLoading || me.isLoading) {
    return <div className="h-screen grid place-items-center text-subtle-foreground text-sm">Loading…</div>;
  }
  if (setup.data && !setup.data.seeded) {
    return <Navigate to="/setup" replace />;
  }
  if (!me.data) {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }
  return <>{children}</>;
}
